/**
 * Payment tracking router
 * @package @app/api
 */

import { z } from 'zod';
import { protectedProcedure } from '../middleware/auth';
import { captureServerEvent } from '../lib/analytics';

export const paymentRouter = {
  create: protectedProcedure
    .input(
      z.object({
        dealId: z.string(),
        amount: z.number().positive(),
        currency: z.string().default('USD'),
        dueDate: z.string().datetime().optional(),
        method: z.string().optional(),
        notes: z.string().optional(),
      }),
    )
    .handler(async ({ context, input }) => {
      const { createId } = await import('@app/db');
      const { db } = context;
      const userId = context.session.user.id;

      // Verify deal ownership
      const deal = await db.deal.findFirst({
        where: { id: input.dealId, athleteId: userId },
      });
      if (!deal) throw new Error('Deal not found');

      const payment = await db.payment.create({
        data: {
          id: createId(),
          ...input,
          dueDate: input.dueDate ? new Date(input.dueDate) : undefined,
          userId,
        },
      });

      await captureServerEvent({
        distinctId: userId,
        event: 'payment_recorded',
        properties: {
          paymentId: payment.id,
          dealId: input.dealId,
          amount: input.amount,
          currency: input.currency,
        },
      });

      return payment;
    }),

  markReceived: protectedProcedure
    .input(
      z.object({
        id: z.string(),
        receivedDate: z.string().datetime().optional(),
        reference: z.string().optional(),
      }),
    )
    .handler(async ({ context, input }) => {
      const { db } = context;
      const payment = await db.payment.findFirst({
        where: { id: input.id, userId: context.session.user.id },
      });
      if (!payment) throw new Error('Payment not found');

      const updated = await db.payment.update({
        where: { id: input.id },
        data: {
          status: 'RECEIVED',
          receivedDate: input.receivedDate ? new Date(input.receivedDate) : new Date(),
          reference: input.reference,
        },
      });

      await captureServerEvent({
        distinctId: context.session.user.id,
        event: 'payment_received',
        properties: {
          paymentId: input.id,
          dealId: payment.dealId,
          amount: payment.amount,
          currency: payment.currency,
        },
      });

      return updated;
    }),

  list: protectedProcedure
    .input(
      z.object({
        dealId: z.string().optional(),
        status: z.enum(['PENDING', 'RECEIVED', 'OVERDUE', 'CANCELLED']).optional(),
        page: z.number().int().positive().default(1),
        limit: z.number().int().positive().max(100).default(20),
      }),
    )
    .handler(async ({ context, input }) => {
      const { db } = context;
      const where: Record<string, unknown> = { userId: context.session.user.id };
      if (input.dealId) where.dealId = input.dealId;
      if (input.status) where.status = input.status;

      const [payments, total] = await Promise.all([
        db.payment.findMany({
          where,
          orderBy: { dueDate: 'asc' },
          skip: (input.page - 1) * input.limit,
          take: input.limit,
          include: {
            deal: { select: { id: true, title: true, brandName: true } },
          },
        }),
        db.payment.count({ where }),
      ]);

      return {
        payments: payments.map((p) => ({
          id: p.id,
          amount: Number(p.amount),
          currency: p.currency,
          status: p.status,
          dueDate: p.dueDate?.toISOString() ?? null,
          receivedDate: p.receivedDate?.toISOString() ?? null,
          method: p.method,
          notes: p.notes,
          dealId: p.deal.id,
          dealTitle: p.deal.title,
          brandName: p.deal.brandName,
          createdAt: p.createdAt.toISOString(),
        })),
        total,
        page: input.page,
        totalPages: Math.ceil(total / input.limit),
      };
    }),

  summary: protectedProcedure.handler(async ({ context }) => {
    const { db } = context;
    const userId = context.session.user.id;

    const [received, pending, overdue] = await Promise.all([
      db.payment.aggregate({
        where: { userId, status: 'RECEIVED' },
        _sum: { amount: true },
      }),
      db.payment.aggregate({
        where: { userId, status: 'PENDING' },
        _sum: { amount: true },
      }),
      db.payment.aggregate({
        where: { userId, status: 'OVERDUE' },
        _sum: { amount: true },
      }),
    ]);

    return {
      received: Number(received._sum.amount ?? 0),
      pending: Number(pending._sum.amount ?? 0),
      overdue: Number(overdue._sum.amount ?? 0),
    };
  }),
};
